﻿declare global {
  interface Window {
    hookUp: (domElement: HTMLInputElement, domTarget: HTMLElement) => void;
    hookUpSweep: (domElement: HTMLElement) => void;
    submit_clear: (formQuerySelector: string, contentQuerySelector: string) => void;
    form_groups_validity: (container: string) => void;
    articlesUpdate: (index: number, delta: number, max: number) => void;
    articlesAdd: (name: string) => void;
    mapShow: (
      mapDiv: HTMLDivElement,
      optionsDiv: HTMLDivElement,
      idInput: HTMLInputElement,
      nameInput: HTMLInputElement,
      postUrl: string,
      options: MapOption[]
    ) => void;
    mapCleanup: (self: HTMLButtonElement) => void;
  }
}
declare var window: Window;

import * as htmx from 'htmx.org';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { markerClusterGroup } from 'leaflet';

htmx.config.includeIndicatorStyles = false;

function translate(english: string, romanian: string): string {
  return document.documentElement.getAttribute('lang') === 'en' ? english : romanian;
}

function h(tag: string, ...childrenOrProps: (Record<string, unknown> | HTMLElement | string)[]): HTMLElement {
  const splitClass = tag.split('.');
  const splitId = splitClass[0].split('#');
  tag = splitId[0];
  const element = document.createElement(tag);
  for (const childrenOrProp of childrenOrProps) {
    if (typeof childrenOrProp === 'string') {
      element.appendChild(document.createTextNode(childrenOrProp));
    } else if (childrenOrProp instanceof HTMLElement) {
      element.appendChild(childrenOrProp);
    } else {
      for (const key in childrenOrProp) {
        element[key] = childrenOrProp[key];
      }
    }
  }
  if (splitId.length > 1) {
    element.id = splitId[1];
  }
  if (splitClass.length > 1) {
    element.className = `${splitClass.slice(1).join(' ')} ${element.className}`.trim();
  }
  return element;
}

document.body.addEventListener('showCaptcha', () => {
  const element = document.querySelector('.h-captcha') as HTMLDivElement;
  if (!element) {
    return;
  }
  if (element.dataset.hooked) {
    window['hcaptcha'].reset();
  } else {
    window['hcaptcha'].render('h-captcha', { sitekey: element.dataset.sitekey });
    element.dataset.hooked = 'true';
  }
});

// region Form Handling

window.hookUp = (domElement: HTMLInputElement, domTarget: HTMLElement): void => {
  if (domElement.dataset.hooked) {
    return;
  }
  domElement.dataset.hooked = 'true';

  const listener = (): void => {
    if (domElement.checkValidity()) {
      domTarget.classList.add('hidden');
      domElement.closest('.form-group').classList.remove('invalid');
    } else {
      domTarget.classList.remove('hidden');
      domElement.closest('.form-group').classList.add('invalid');
    }
  };

  domElement.addEventListener('input', listener);
  domElement.addEventListener('change', listener);
};

window.hookUpSweep = (domElement: HTMLElement): void => {
  domElement
    .closest('form')
    .querySelectorAll<HTMLInputElement>('input, select')
    .forEach((input) => {
      input.dispatchEvent(new Event('focus', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
};

window.submit_clear = (formQuerySelector: string, contentQuerySelector: string): void => {
  const form = document.querySelector(formQuerySelector);
  const content = document.querySelector(contentQuerySelector);

  form.addEventListener('submit', () => {
    content.innerHTML = '';
  });
};

window.form_groups_validity = (container: string): void => {
  document
    .querySelector(container)
    .querySelectorAll('.form-group')
    .forEach((formGroup: HTMLDivElement) => {
      formGroup.querySelectorAll('input, select').forEach((is: HTMLInputElement | HTMLSelectElement) => {
        is.addEventListener('input', function () {
          if (is.validity.valid) {
            formGroup.classList.remove('error');
          } else {
            formGroup.classList.add('error');
          }
        });
      });
    });
};

document.querySelectorAll('input, select').forEach((input) => {
  input.addEventListener(
    'input',
    () => {
      window.onbeforeunload = () => true;
    },
    { once: true }
  );
});

// endregion

// region Articles

type ArticlesType = {
  Quantity: number;
  Description: string;
  CountryOfOrigin: null | string;
  Tariff: null | string;
  WeightNet: null | number;
  WeightGross: number;
  PricePerPieceAmount: number;
  PricePerPieceAmountCurrency: null | string;
  Sku: null | string;
}[];

window.articlesUpdate = (index: number, delta: number, max: number): void => {
  const articlesDom = document.querySelector('#reviewMain #PriceModelViewModel_Articles') as HTMLInputElement;
  const textDom = document.querySelector(`#itemCount${index}`) as HTMLSpanElement;
  const checkedDom = document.querySelector(`#itemChecked${index}`) as HTMLInputElement;

  const value = +textDom.textContent;
  const newValue = Math.min(max, Math.max(0, value + delta));

  textDom.textContent = newValue.toString();
  checkedDom.checked = newValue > 0;
  checkedDom.dispatchEvent(new Event('change', { bubbles: true }));

  const articles = JSON.parse(articlesDom.value) as ArticlesType;
  articles[index].Quantity = newValue;
  articlesDom.value = JSON.stringify(articles);
  articlesDom.dispatchEvent(new Event('input', { bubbles: true }));
};

window.articlesAdd = (name: string): void => {
  const articles = document.querySelector('#reviewMain #PriceModelViewModel_Articles') as HTMLInputElement;
  const articlesContainer = document.querySelector('#articlesContainer') as HTMLDivElement;
  const articlesValue = JSON.parse(articles.value ? articles.value : '[]') as ArticlesType;
  articlesValue.push({
    Quantity: 1,
    Description: name,
    CountryOfOrigin: null,
    PricePerPieceAmount: 0,
    PricePerPieceAmountCurrency: null,
    Sku: null,
    Tariff: null,
    WeightGross: 0,
    WeightNet: 0,
  });
  articles.value = JSON.stringify(articlesValue);
  articles.dispatchEvent(new Event('input', { bubbles: true }));

  const node = h(
    'div.flex.items-center.justify-between.border.p-4.shadow-sm.article',
    h('div.flex.items-center.space-x-4', h('div.max-w-64', h('h3.text-md.font-medium', name))),
    h(
      'div.flex.items-center.space-x-4',
      h(
        'div.flex.items-center.space-x-2',
        h(
          'button.secondary.!border-secondary.!text-secondary.!px-2.!py-0',
          { type: 'button', onclick: () => window.articlesUpdate(articlesValue.length - 1, -1, 1) },
          '-'
        ),
        h('span.w-8.text-center', h(`span#itemCount${articlesValue.length - 1}`, '1'), '/1'),
        h(
          'button.secondary.!border-secondary.!text-secondary.!px-2.!py-0',
          { type: 'button', onclick: () => window.articlesUpdate(articlesValue.length - 1, 1, 1) },
          '+'
        )
      ),
      h('input.form-checkbox.!h-7.!w-7.before:!h-7.before:!w-7.text-blue-600.!pointer-events-none', {
        type: 'checkbox',
        id: `itemChecked${articlesValue.length - 1}`,
        checked: true,
      })
    )
  );

  articlesContainer.appendChild(node);
};

// endregion

// region Reactivity
const DEBUG = false;

class State<TObject extends object> {
  constructor(private state: TObject) {
    if (DEBUG) console.log('[STATE Created]', state);
  }

  update<T extends keyof TObject>(key: T, value: TObject[T]) {
    if (DEBUG) console.log('[STATE Update]', key, 'set', value);
    this.state[key] = value;
  }

  get<T extends keyof TObject>(key: T) {
    if (DEBUG) console.log('[STATE Get]', key, 'is', this.state[key]);
    return this.state[key] as TObject[T];
  }
}

class Events<TEventKey extends string | number | symbol> {
  private events: { [k in TEventKey]?: (() => void)[] } = {};

  on(key: TEventKey, callback: () => void) {
    if (!this.events[key]) {
      this.events[key] = [];
    }
    if (DEBUG) console.log('[EVENTS Added]', key, 'push', callback);
    this.events[key].push(callback);
    return this;
  }

  emit(key: TEventKey) {
    if (!this.events[key]) {
      return;
    }
    if (DEBUG) console.group('[EVENTS Emit]', key);
    for (const callback of this.events[key]) {
      callback();
    }
    if (DEBUG) console.groupEnd();
    return this;
  }

  clean() {
    this.events = {};
    return this;
  }
}

// endregion

// region Map

interface MapOption {
  addressText: string;
  courier: { id: number; name: string };
  email: null | string;
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  phone: null | string;
  postalCode: string;
  schedule: null | { day: number; openingHour: string; closingHour: string }[];
  supportedPaymentType: number;
  type: 1 | 2;
}

window.mapShow = (
  mapDiv: HTMLDivElement,
  optionsDiv: HTMLDivElement,
  idInput: HTMLInputElement,
  nameInput: HTMLInputElement,
  postUrl: string,
  options: MapOption[]
): void => {
  const leafletMap = L.map(mapDiv);
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(leafletMap);

  const bounds = L.latLngBounds(options.map((option) => [option.latitude, option.longitude]));
  const group = markerClusterGroup({ disableClusteringAtZoom: 18 });
  let selectedOption: null | MapOption = null;
  const markerOptions = [] as L.Marker[];

  function updateSelectedOptionDiv() {
    function getDayTranslation(day: number): string {
      const english = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const romanian = ['', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sambătă', 'Duminică'];
      return document.documentElement.getAttribute('lang') === 'en' ? english[day] : romanian[day];
    }

    optionsDiv.parentNode.querySelector('.absolute')?.remove();
    if (selectedOption) {
      const overlay = h(
        'div#overlay.absolute.top-0.right-0.left-0.bottom-0.bg-white.text-xs.p-2',
        h('h3.text-lg.font-bold', selectedOption.name),
        h('p.font-light', selectedOption.courier.name),
        h('p.text-sm.py-2', selectedOption.addressText + ', ' + selectedOption.postalCode),
        h('p', selectedOption.email),
        h('p', selectedOption.phone),
        h(
          'table.w-full',
          // h("thead", h("tr", h("th.text-left", "Day"), h("th.text-left", "From"), h("th.text-left", "To"))),
          h(
            'tbody',
            ...(selectedOption.schedule ?? []).map((s) =>
              h(
                'tr.tabular-nums',
                h('td.text-left', getDayTranslation(s.day)),
                h('td.text-left', s.openingHour),
                h('td.text-left', s.closingHour)
              )
            )
          )
        ),
        h(
          'button.w-full.my-3.secondary',
          {
            type: 'button',
            onclick: function () {
              selectedOption = null;
              updateSelectedOptionDiv();
            },
          },
          translate('Select a different fixed point', 'Alege alt punct de ridicare')
        ),
        h(
          'button.w-full.mb-3',
          {
            type: 'button',
            onclick: function () {
              const id = '#' + mapDiv.closest('.input-container').parentElement.id;
              const d = h('div');
              d.setAttribute('hx-target', id);
              d.setAttribute('hx-select', id);
              d.setAttribute('hx-trigger', 'click');
              d.setAttribute('hx-swap', 'outerHTML');
              d.setAttribute('hx-params', '__RequestVerificationToken,id');
              d.setAttribute('hx-vals', '{"id":"' + selectedOption.id + '"}');
              d.setAttribute('hx-post', postUrl);
              mapDiv.appendChild(d);

              htmx.process(d);
              htmx.trigger(d, 'click', null);

              document.body.classList.remove('overflow-hidden');
              idInput.value = selectedOption.id.toString();
              nameInput.value = selectedOption.name.toString();
              idInput.value = selectedOption.id.toString();
              idInput.dispatchEvent(new Event('input'));
            },
          },
          translate('Select this fixed point', 'Alege acest punct de ridicare')
        )
      );
      optionsDiv.parentNode.appendChild(overlay);
    }
  }

  for (const option of options) {
    const marker = L.marker([option.latitude, option.longitude]);
    const selectDivDom = h(
      'div.border-b.p-2.cursor-pointer.text-sm',
      {
        onclick: () => {
          leafletMap.flyTo(marker.getLatLng(), 18, { duration: 0.5 });
          leafletMap.once('zoomend', () => {
            marker.openPopup();
          });
          selectedOption = option;
          updateSelectedOptionDiv();
        },
      },
      '[' + option.courier.name + ']: ' + option.name
    );
    optionsDiv.appendChild(selectDivDom);
    marker.bindPopup('[' + option.courier.name + ']: ' + option.name);
    marker.on('popupopen', () => {
      leafletMap.flyTo(marker.getLatLng(), 18, { duration: 0.5 });
      selectedOption = option;
      updateSelectedOptionDiv();
    });
    marker.on('popupclose', () => {
      selectedOption = null;
      updateSelectedOptionDiv();
    });
    group.addLayer(marker);
    markerOptions.push(marker);
  }
  leafletMap.addLayer(group);
  leafletMap.invalidateSize();
  if (bounds.isValid()) setTimeout(() => leafletMap.fitBounds(bounds));
  document.body.classList.add('overflow-hidden');
  mapDiv
    .closest('.map-container')
    .querySelector('.input-fixed-point')
    .addEventListener('input', function () {
      const searchString = this.value.trim().toLowerCase();
      for (let i = optionsDiv.children.length - 1; i >= 0; i--) {
        optionsDiv.children[i].classList.add('hidden');
        if (!searchString || optionsDiv.children[i].textContent.toLowerCase().indexOf(searchString) > 0) {
          optionsDiv.children[i].classList.remove('hidden');
        }
      }
    });
};

window.mapCleanup = (self: HTMLButtonElement): void => {
  self.closest('.map-container').innerHTML = '';
  document.body.classList.remove('overflow-hidden');
};

// endregion
